import { useEffect, useState } from 'react';
import { onSnapshot, type Unsubscribe } from 'firebase/firestore';
import type { QBExpense } from '../types';
import { qbExpensesProjectPLQuery } from '../utils/qbExpensesQueries';

type QBExpenseRow = QBExpense & { id: string };

let sharedRows: QBExpenseRow[] = [];
let sharedUnsub: Unsubscribe | null = null;
let subscriberCount = 0;
const listeners = new Set<(rows: QBExpenseRow[]) => void>();

function notifyListeners(): void {
  listeners.forEach(fn => fn(sharedRows));
}

function ensureSharedListener(): void {
  if (sharedUnsub) return;
  sharedUnsub = onSnapshot(
    qbExpensesProjectPLQuery(),
    snap => {
      sharedRows = snap.docs.map(d => ({ id: d.id, ...(d.data() as object) } as QBExpenseRow));
      notifyListeners();
    },
    err => {
      console.error('[useQbExpensesProjectPL] qbExpenses listen failed:', err);
      sharedRows = [];
      notifyListeners();
    },
  );
}

function releaseSharedListener(): void {
  if (subscriberCount > 0) return;
  sharedUnsub?.();
  sharedUnsub = null;
}

/** Ref-counted listener — same expense set Project P&L uses (splits + pending links). */
export function useQbExpensesProjectPL(): QBExpenseRow[] {
  const [rows, setRows] = useState<QBExpenseRow[]>(sharedRows);

  useEffect(() => {
    subscriberCount += 1;
    listeners.add(setRows);
    ensureSharedListener();
    setRows(sharedRows);
    return () => {
      listeners.delete(setRows);
      subscriberCount -= 1;
      releaseSharedListener();
    };
  }, []);

  return rows;
}
